import { capitalize, cloneDeep, isEmpty, map } from 'lodash'
import pMap from 'p-map'
import { isNode } from 'reactflow'
import { v4 } from 'uuid'

import {
  ActionConfigProps,
  APIRequestStepData,
  BasicValidationError,
  CustomNodes,
  Feature,
  FlowElement,
  RelatedFeature,
  RepeatOptions,
  Step,
  StepType,
  TriggerTypes,
  Workflow
} from '@/pages/SpaceAppBuilder/utilities/interfaces'

import actionsConfig from './Operations/DataTransform/actionsConfig.json'
import getValidationSchema from './Operations/getValidationSchema'

export const isSelectableNode = (element: FlowElement) => {
  return (
    isNode(element) &&
    element.type !== CustomNodes.placeHolderNode &&
    element.data.config.type !== 'none' &&
    element.data.config.type !== TriggerTypes.notificationAction
  )
}

export const findPath = (object: any, value: string): any => {
  for (const key in object) {
    if (Object.hasOwn(object, key)) {
      // if it's an owned key
      if (value === object[key] && key === 'id') return key
      // if the item beign searched is at this key then return this key as the path
      else if (object[key] && typeof object[key] === 'object') {
        // otherwise if the item at this key is also an object
        const path = findPath(object[key], value)
        // search for the item a in that object
        if (path) return (Number.isNaN(Number(key)) ? key : '[' + key + ']') + '.' + path // if found then the path is this key followed by the result of the search
      }
    }
  }
}

export const findNestedObject = (object: any, keyToFind: string, valToFind: string) => {
  let foundObj
  JSON.stringify(object, (_, nestedValue) => {
    if (nestedValue && nestedValue[keyToFind] === valToFind) {
      foundObj = nestedValue
    }
    return nestedValue
  })
  return foundObj
}

export const removeLastPath = (path: string) => {
  return path?.slice(0, path.lastIndexOf('.'))
}

export const constructOnSuccessChain = (featureSteps: Step[]) => {
  return traverseSteps(featureSteps, (step, steps, index) => {
    if (steps.length - 1 !== index) {
      return {
        ...step,
        onSuccess: [steps[index + 1].id]
      }
    } else if (steps.length - 1 === index && steps[index].onSuccess) {
      delete steps[index].onSuccess
    }
    return step
  })
}

export const substituteVariables = (str: string, mapObj: any) => {
  //replace $.step-1. with $.{guid}. on outgoing requests
  const regex = '(?<=\\$[.])' + Object.keys(mapObj)?.reverse().join('|')
  return str.replaceAll(new RegExp(regex, 'gi'), (matched: string) => {
    return mapObj[matched]
  })
}

export const transformVariableData = (steps: Step[], incomingData: boolean) => {
  const mapData = {} as Record<string, string>

  traverseSteps(steps, step => {
    if (incomingData) {
      mapData[`${step.id}`] = `step-${step.stepCount}`
    } else if (step.type === StepType.Loop) {
      mapData[`step-${step.stepCount}.key`] = `${step.id}.key`
      mapData[`step-${step.stepCount}.output`] = `${step.id}.output`
    } else {
      mapData[`step-${step.stepCount}.output`] = `${step.id}.output`
    }
    return step
  })

  return traverseSteps(steps, step => {
    return {
      ...step,
      stepData: {
        ...JSON.parse(substituteVariables(JSON.stringify(step.stepData) || '{}', mapData)),
        ...(step.stepData?.loopSteps && { loopSteps: step.stepData.loopSteps }),
        ...(step.stepData?.onTrue && { onTrue: step.stepData.onTrue }),
        ...(step.stepData?.onFalse && { onFalse: step.stepData.onFalse })
      }
    }
  })
}

export const removeStepCount = (steps: Step[]) => {
  return traverseSteps(steps, (step: Step) => {
    delete step.stepCount
    return step
  })
}

export const assignStepCount = (steps: Step[]) => {
  let stepCount = 1
  const buildStepCount = (steps: Step[]) => {
    return steps?.map((step: Step) => {
      let parentStepCount: number | undefined
      if (step.type === StepType.Boolean) {
        parentStepCount = stepCount
        stepCount++
        step.stepData.onTrue = buildStepCount(step.stepData.onTrue)
        step.stepData.onFalse = buildStepCount(step.stepData.onFalse)
      } else if (step.type === StepType.Loop) {
        parentStepCount = stepCount
        stepCount++
        step.stepData.loopSteps = buildStepCount(step.stepData.loopSteps)
      } else {
        parentStepCount = undefined
      }
      return {
        ...step,
        stepCount: parentStepCount || stepCount++
      }
    })
  }
  return buildStepCount(steps)
}

export const convertFeaturesToRelatedFeatures = (features: Feature[]): Partial<RelatedFeature>[] => {
  return features.map((feature: Feature) => ({ featureId: feature.id, name: feature.name }))
}

const traverseStepsAsync = async (steps: Step[], fn: (step: Step, index: number) => void) => {
  const traverse = async (steps: Step[]) => {
    return await pMap(steps, async (step: Step, index: number) => {
      if (step.type === StepType.Boolean) {
        step.stepData.onTrue = await traverse(step.stepData.onTrue)
        step.stepData.onFalse = await traverse(step.stepData.onFalse)
      } else if (step.type === StepType.Loop) {
        step.stepData.loopSteps = await traverse(step.stepData.loopSteps)
      }
      await fn(step, index)
    })
  }
  return await traverse(steps)
}

export const traverseSteps = (steps: Step[], fn: (step: Step, steps: Step[], index: number) => Step): Step[] => {
  const traverse = (steps: Step[]) => {
    return map(steps, (step: Step, index: number) => {
      if (step.type === StepType.Boolean) {
        step.stepData.onTrue = traverse(step.stepData.onTrue)
        step.stepData.onFalse = traverse(step.stepData.onFalse)
      } else if (step.type === StepType.Loop) {
        step.stepData.loopSteps = traverse(step.stepData.loopSteps)
      }
      return fn(step, steps, index)
    })
  }
  return traverse(steps)
}

export function flattenFeatureSteps(steps: Step[]) {
  const flattenedFeatureSteps: Step[] = []
  const buildFlatSteps = (steps: Step[]) => {
    steps?.forEach(step => {
      if (step.type === StepType.Boolean) {
        buildFlatSteps(step.stepData.onTrue || [])
        buildFlatSteps(step.stepData.onFalse || [])
      }
      if (step.type === StepType.Loop) {
        buildFlatSteps(step.stepData.loopSteps || [])
      }
      flattenedFeatureSteps.push(step)
    })
  }
  buildFlatSteps(steps)
  return flattenedFeatureSteps
}

export async function validateNodes(
  elements: FlowElement[],
  workflow: Workflow,
  isCompleted: boolean,
  features: Feature[] | undefined
) {
  let workflowIsValid = true
  let runValidationErrors = [] as BasicValidationError[]

  const validatableNodes = elements.reduce(
    (acc: { triggerNode: BasicValidationError | {}; steps: BasicValidationError[] }, element: FlowElement) => {
      if (element.type === CustomNodes.triggerNode) {
        acc.triggerNode = { key: element.type, id: element.id }
      } else if (element.type === CustomNodes.stepNode) {
        acc.steps.push({ key: element.type, id: element.id })
      }
      return acc
    },
    { triggerNode: {}, steps: [] }
  )

  if (Object.keys(validatableNodes.triggerNode).length > 0) {
    const triggerToValidate = cloneDeep(workflow.trigger)
    // If scheduled trigger is not configured, set repeats value in triggerData to force validation rules
    if (triggerToValidate.type === TriggerTypes.scheduled && !triggerToValidate.triggerData) {
      // @ts-expect-error
      triggerToValidate.triggerData = {
        repeats: RepeatOptions.OneTime
      }
    }

    const triggerValid = await getValidationSchema({ type: triggerToValidate.type }).isValid(triggerToValidate)
    if (!triggerValid && triggerToValidate.type !== TriggerTypes.notificationAction) {
      runValidationErrors = [...runValidationErrors, validatableNodes.triggerNode] as BasicValidationError[]
      workflowIsValid = false
    }
  }

  if (validatableNodes.steps.length > 0 && isCompleted) {
    await traverseStepsAsync(cloneDeep(workflow.steps), async (step: Step) => {
      let stepValid = await getValidationSchema({ type: step.type }).isValid(step)

      if (step.type === StepType.RunFeature) {
        const associatedFeature = features?.find(
          (feature: Feature) => feature.id === step.stepData.featureId?.replace('$.feature.', '')
        )
        if (!associatedFeature || associatedFeature.type !== TriggerTypes.featureTrigger) {
          stepValid = false
          workflowIsValid = false
        }
      }
      const stepNode = validatableNodes.steps.find(node => node.id === step.id)
      // verify the step doesn't refer to a variable declared later in the workflow due to drag and drop
      const matchedSteps = JSON.stringify(step.stepData).match(/\$.step-\d/g)

      if (matchedSteps && stepValid) {
        matchedSteps.forEach(matchedStep => {
          const variableStepNumber = matchedStep.match(/\d+/)
          if (variableStepNumber?.length && step.stepCount) {
            stepValid = Number(variableStepNumber[0]) < step.stepCount
          }
        })
      }

      if ((!stepValid || Object.keys(step.stepData).length === 0) && stepNode) {
        const stepNode = validatableNodes.steps.find(node => node.id === step.id)
        runValidationErrors = [...runValidationErrors, stepNode] as BasicValidationError[]
        workflowIsValid = false
      }
    })
  } else {
    if (isEmpty(workflow.steps)) {
      workflowIsValid = false
    }
  }
  return { runValidationErrors, workflowIsValid }
}

export function fetchNotificationStepIds(step: Step) {
  const actionFeatureStepIds: { stepId: string }[] = []

  const steps = [step]

  const findNotificationStepIds = (steps: Step[]) => {
    steps?.forEach(step => {
      if (step.type === StepType.Boolean) {
        findNotificationStepIds(step.stepData.onTrue || [])
        findNotificationStepIds(step.stepData.onFalse || [])
      }
      if (step.type === StepType.Loop) {
        findNotificationStepIds(step.stepData.loopSteps || [])
      }

      if (step.type === StepType.Notification) {
        actionFeatureStepIds.push({ stepId: step.id })
      }
    })
  }

  findNotificationStepIds(steps)
  return actionFeatureStepIds
}

export const getDataTransformActionConfig = (actionName: string | undefined): ActionConfigProps | undefined => {
  return actionsConfig.find(ac => ac.name === actionName)
}
export const getRequestPreview = (stepData: APIRequestStepData) => {
  let requestPreview = ''
  if (stepData.path) {
    // Strip whitespace from path - if variables are injected.
    const stepDataPath = stepData.path.replaceAll(/\s/g, '')
    stepDataPath.startsWith('/') ? (requestPreview = stepDataPath) : (requestPreview = '/' + stepDataPath)
    const queryParams = stepData.input.query

    if (queryParams && !isEmpty(queryParams)) {
      requestPreview = requestPreview.concat('', '?')
      for (const query of queryParams) {
        if (!isEmpty(query.key) && !isEmpty(query.value)) {
          const separator = requestPreview.endsWith('?') ? '' : '&'
          const queryParam = query.key.concat('=', query.value)
          requestPreview = requestPreview.concat(separator, queryParam)
        }
      }
    }
  }

  return requestPreview
}

export const updateDuplicateStepIDs = (steps: Step[]) => {
  for (const step of steps) {
    step.id = v4()
    const stepData = step.stepData
    if (step.type === StepType.Boolean) {
      step.stepData.onTrue = updateDuplicateStepIDs(stepData.onTrue)
      step.stepData.onFalse = updateDuplicateStepIDs(stepData.onFalse)
    } else if (step.type === StepType.Loop) {
      step.stepData.loopSteps = updateDuplicateStepIDs(stepData.loopSteps)
    }
  }
  return steps
}

export const updateNestedStepVariableReferences = (steps: Step[], duplicateStepIndex: number) => {
  const duplicateStep = steps[duplicateStepIndex]
  const originalStep = steps[duplicateStepIndex - 1]
  const stepType = originalStep.type

  let nestedStepCounts: number[] = []
  traverseSteps([originalStep], step => {
    if (step.stepCount) {
      nestedStepCounts = nestedStepCounts.concat([step.stepCount])
    }
    return step
  })

  if (!isEmpty(nestedStepCounts)) {
    // map over nested steps that have been duplicated
    // calculate and replace the corresponding step number under the new duplicated parent step
    const mapData = {} as Record<string, string>
    traverseSteps(steps, step => {
      let stepNumber = step.stepCount
      if (step.stepCount === originalStep.stepCount) {
        stepNumber = duplicateStep.stepCount
      } else if (nestedStepCounts.includes(step.stepCount!)) {
        stepNumber = step.stepCount! - originalStep.stepCount! + duplicateStep.stepCount!
      }
      mapData[`step-${step.stepCount}`] = `step-${stepNumber}`
      return step
    })

    if (stepType === StepType.Loop) {
      steps[duplicateStepIndex].stepData.loopSteps = traverseStepsAndSubstituteVariables(
        duplicateStep.stepData.loopSteps,
        mapData
      )
    } else if (stepType === StepType.Boolean) {
      steps[duplicateStepIndex].stepData.onTrue = traverseStepsAndSubstituteVariables(
        duplicateStep.stepData.onTrue,
        mapData
      )
      steps[duplicateStepIndex].stepData.onFalse = traverseStepsAndSubstituteVariables(
        duplicateStep.stepData.onFalse,
        mapData
      )
    }
  }
  return steps
}

const traverseStepsAndSubstituteVariables = (steps: Step[], mapData: Record<string, string>) => {
  return traverseSteps(steps, step => {
    return {
      ...step,
      stepData: {
        ...JSON.parse(substituteVariables(JSON.stringify(step.stepData) || '{}', mapData)),
        ...(step.stepData?.loopSteps && { loopSteps: step.stepData.loopSteps }),
        ...(step.stepData?.onTrue && { onTrue: step.stepData.onTrue }),
        ...(step.stepData?.onFalse && { onFalse: step.stepData.onFalse })
      }
    }
  })
}

export const kebabToCapitalized = (text: string) => {
  const kebabToSpace = text.replaceAll('-', ' ')
  const camelToSpace = kebabToSpace.replaceAll(/([a-z])([A-Z])/g, '$1 $2')
  return capitalize(camelToSpace)
}
